import React from "react";
import { Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { bulkDelete } from "../lib/multiSelect";
import { useConfirm } from "./ConfirmDialog";
import { useAuth } from "../lib/auth";

/**
 * Floating action bar shown when >=1 items are selected.
 * Admin-only visibility is enforced here — safe to always render.
 */
export default function MultiSelectBar({ count, resource, ids, onCleared, label = "items", extraActions = null }) {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  if (!user || user.role !== "admin") return null;
  if (!count) return null;

  const doDelete = async () => {
    const ok = await confirm({
      title: `Delete ${count} ${label}?`,
      message: `You're about to permanently remove ${count} ${label}. This cannot be undone.`,
      tone: "danger",
      confirmLabel: `Delete ${count}`,
    });
    if (!ok) return;
    try {
      const r = await bulkDelete(resource, ids);
      toast.success(`${r.deleted} ${label} deleted`);
      onCleared?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  return (
    <div
      data-testid="multiselect-bar"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 bg-zinc-900 text-white shadow-2xl border border-zinc-700 rounded-sm animate-scalein"
    >
      <span className="text-xs font-mono">
        <span className="font-black text-lg mr-1">{count}</span> {label} selected
      </span>
      {extraActions}
      <button
        onClick={doDelete}
        data-testid="multiselect-delete"
        className="ml-2 flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-bold uppercase tracking-widest transition-colors"
      >
        <Trash size={14} weight="bold" /> Delete
      </button>
      <button
        onClick={onCleared}
        className="text-white/60 hover:text-white ml-1"
        title="Clear selection"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
